import { useIsFocused, useNavigationState } from '@react-navigation/native';
import { useRecoilValue } from 'recoil';

import {
  AlertModalTypes,
  alertsModalPageTestIDs,
  currentWeatherConditionsPageTestIDs,
  dailyWeatherPageTestIDs,
  hourlyWeatherPageTestIDs,
  livePageTestIDs,
  mapPageTestIDs,
  myWeatherPageTestIDs,
} from '@constants';
import { navigationService } from '@services';
import { hdmiConnectedState } from '@store';

// Places where live video can be displayed.
export const VideoContext = {
  live: livePageTestIDs.livePage, // Full screen
  myWeather: myWeatherPageTestIDs.myWeatherPage, // Tile
  currentConditions: currentWeatherConditionsPageTestIDs.currentWeatherConditionsPage, // PIP
  hourlyForecast: hourlyWeatherPageTestIDs.hourlyWeatherPage, // PIP
  dailyForecast: dailyWeatherPageTestIDs.dailyWeatherPage, // PIP
  map: mapPageTestIDs.mapPage, // PIP
};

// Parses the navigation state returned by useNavigationState((state) => state).
const navigationStack = navigationState =>
  navigationState?.map(route => {
    const routeState = route.state;
    const routeName = routeState == null ? route.name : routeState.routes[routeState.index].name;

    if (routeName === alertsModalPageTestIDs.alertsModalPage) {
      return route.params.type;
    }
    return routeName;
  });

export const useIsVideoFocused = videoContext => {
  // This doesn't always have the correct navigation state (we call getCurrentStack
  // below to get that), but this will trigger the re-render we need.
  useNavigationState(state => state);
  let routes = navigationStack(navigationService.getCurrentStack()) ?? [];

  // If we get a route with ["MainMenu"], then we just launched the app and need to
  // replace it with the default screen (live).
  if (routes.length === 0 || (routes.length === 1 && routes[0] === 'MainMenu')) {
    routes = [livePageTestIDs.livePage];
  }

  const reversedRoutes = [...routes].reverse();
  const lastRoute = reversedRoutes[0];

  // Other hooks
  const hdmiConnected = useRecoilValue(hdmiConnectedState);
  const pageIsVisible = useIsFocused();

  // Video should never play if hdmi/audio state is disconnected
  if (!hdmiConnected || lastRoute === AlertModalTypes.disconnected) {
    return false;
  }

  // Sometimes useIsFocused will be updated before navigation.  Catch that here and
  // return true if pageIsVisible.  This fixes a bug when navigating back to the
  // My Weather Screen would stop the video when it shouldn't.
  if (pageIsVisible) {
    return true;
  }

  // If `videoContext` isn't being passed in, then default to always show the video.
  if (!videoContext) {
    return true;
  }

  const contextValues = Object.values(VideoContext);

  let isFocused = false;

  for (const routeName of reversedRoutes) {
    if (contextValues.includes(routeName)) {
      // This route is one of the screens video can be displayed.
      // Check the route name to see if it's the video context we are interested in.
      isFocused = routeName === videoContext;

      // We only want to check the top-most route that matches, so break out of the loop here.
      break;
    }
  }

  return isFocused;
};
